(ns heraldicon.svg.core
  (:require
   [clojure.string :as str]
   [clojure.walk :as walk]
   [com.wsscode.async.async-cljs :refer [<? go-catch]]
   [heraldicon.util.uid :as uid]
   [heraldicon.util.walk :as util.walk]))

(defn- split-style-value [value]
  (into {}
        (keep (fn [chunk]
                (let [[k v] (str/split chunk #":" 2)
                      k (some-> k str/trim)
                      v (some-> v str/trim)]
                  (when-not (or (str/blank? k)
                                (str/blank? v))
                    [(keyword k) v]))))
        (str/split value #";")))

(defn fix-string-style-values [data]
  (walk/postwalk #(if (and (vector? %)
                           (-> % count (= 2))
                           (-> % first (= :style))
                           (-> % second string?))
                    [:style (split-style-value (second %))]
                    %)
                 data))

(defn fix-attribute-and-tag-names [data]
  (walk/postwalk
   (fn [v]
     (get {:pattern-units :patternUnits
           :patternunits :patternUnits
           :patterntransform :patternTransform
           :gradientunits :gradientUnits
           :xlink:href :href
           :gradienttransform :gradientTransform
           :lineargradient :linearGradient
           :radialgradient :radialGradient
           :preserveaspectratio :preserveAspectRatio
           :xml:space :xmlSpace
           :calcmode :calcMode
           :repeatcount :repeatCount
           :attributename :attributeName
           :filterunits :filterUnits
           :clippathunits :clipPathUnits
           :primitiveunits :primitiveUnits
           :stddeviation :stdDeviation
           :horizOriginX :horizoriginx
           :horizOriginY :horizoriginy
           :basefrequency :baseFrequency
           :solidColor :solidcolor
           :requiredextensions :requiredExtensions} v v))
   data))

(defn- svg-namespaced-tag-or-attribute? [tag]
  (and (keyword? tag)
       (-> tag name (str/includes? ":"))))

(defn remove-namespaced-elements [data]
  (walk/postwalk
   (fn [data]
     (cond
       (vector? data) (into []
                            (remove (fn [element]
                                      (and (vector? element)
                                           (-> element first svg-namespaced-tag-or-attribute?))))
                            data)
       (map? data) (into {}
                         (remove (comp svg-namespaced-tag-or-attribute? first))
                         data)
       :else data))

   data))

(defn- replace-id-references [data id-map]
  (let [prepared-id-map (into {}
                              (mapcat (fn [[k v]]
                                        [[k v]
                                         [(str "#" k) (str "#" v)]
                                         [(str "url(#" k ")") (str "url(#" v ")")]]))
                              id-map)]
    (util.walk/postwalk
     #(if (and (vector? %)
               (-> % second string?)
               (-> % first #{:id :href :stroke :fill :mask :clip-path}))
        (if-let [new-ref (->> % second (get prepared-id-map))]
          [(first %) new-ref]
          %)
        %)
     data)))

(defn make-unique-ids
  "Rewrites all ids in data and the references to them. With a prefix, the new
  ids are derived from it instead of generated, so the result is deterministic."
  ([data]
   (make-unique-ids data nil))
  ([data prefix]
   (let [ids (->> data
                  (tree-seq (some-fn map? vector? seq?) seq)
                  (filter #(and (vector? %)
                                (-> % first (= :id))))
                  (map second)
                  set)
         id-map (into {}
                      (map (fn [id]
                             [id (if prefix
                                   (str prefix "-" id)
                                   (uid/generate (str "unique-" id)))]))
                      ids)]
     (replace-id-references data id-map))))

(defn optimize [data svgo-optimize-fn]
  (go-catch
   (-> (svgo-optimize-fn data)
       <?
       (js->clj :keywordize-keys true)
       :data)))

(defn- split-outside-quotes-and-parens
  "Splits s at every separator character that isn't inside quotes or
  parentheses, e.g. the ; in url(data:image/png;base64,...)."
  [s separator]
  (loop [[c & remaining] s
         current []
         parts []
         quote-char nil
         depth 0]
    (cond
      (nil? c) (conj parts (apply str current))
      quote-char (recur remaining (conj current c) parts (when (not= c quote-char) quote-char) depth)
      (#{\" \'} c) (recur remaining (conj current c) parts c depth)
      (= c \() (recur remaining (conj current c) parts nil (inc depth))
      (= c \)) (recur remaining (conj current c) parts nil (max 0 (dec depth)))
      (and (= c separator)
           (zero? depth)) (recur remaining [] (conj parts (apply str current)) nil depth)
      :else (recur remaining (conj current c) parts nil depth))))

(defn- parse-declarations [block]
  (into {}
        (keep (fn [declaration]
                (let [[property value] (str/split declaration #":" 2)
                      property (some-> property str/trim)
                      value (some-> value str/trim)]
                  (when-not (or (str/blank? property)
                                (str/blank? value))
                    [(keyword property) value]))))
        (split-outside-quotes-and-parens block \;)))

(defn- skip-block
  "Returns the index after the block whose { is at start, counting nested braces."
  [css start]
  (loop [index (inc start)
         depth 1]
    (cond
      (>= index (count css)) index
      (zero? depth) index
      :else (recur (inc index)
                   (case (nth css index)
                     \{ (inc depth)
                     \} (dec depth)
                     depth)))))

(defn- parse-css-rules
  "Parses a stylesheet into [selectors declarations] pairs. Only plain rules are
  kept; at-rules like @media or @font-face are skipped."
  [css]
  (let [css (str/replace css #"(?s)/\*.*?\*/" "")]
    (loop [index 0
           rules []]
      (let [open (str/index-of css "{" index)
            prelude (some-> open (->> (subs css index)) str/trim)]
        (cond
          (nil? open) rules
          ;; an at-rule without a block, e.g. @import ...; before the next rule
          (str/includes? prelude ";") (recur (inc (str/index-of css ";" index)) rules)
          (str/starts-with? prelude "@") (recur (skip-block css open) rules)
          :else (let [close (or (str/index-of css "}" open) (count css))
                      selectors (into []
                                      (comp (map str/trim)
                                            (remove str/blank?))
                                      (str/split prelude #","))]
                  (recur (inc close)
                         (conj rules [selectors (parse-declarations (subs css (inc open) close))]))))))))

(defn- read-css-rules [svg-data]
  (let [style-blocks (->> svg-data
                          (tree-seq (some-fn map? vector? seq?) seq)
                          (filter #(and (vector? %)
                                        (-> % count (> 2))
                                        (-> % first (= :style))
                                        (-> % second map?)
                                        (-> % second :type (= "text/css")))))
        css-data (->> style-blocks
                      (map (fn [style]
                             (nth style 2)))
                      (apply str))]
    (parse-css-rules css-data)))

(defn- css-rule-applicable? [selectors tag css-class css-id]
  (some (fn [selector]
          ;; limit to .class, #id, tag; more complex selectors are
          ;; difficult and likely not needed
          (or (= selector (str "." css-class))
              (= selector (str "#" css-id))
              (= selector tag)))
        selectors))

(defn- apply-css-rule [data [selectors declarations]]
  (let [tag (-> data first name)
        css-class (-> data second :class)
        css-id (-> data second :id)]
    (cond-> data
      (css-rule-applicable?
       selectors tag css-class css-id) (update 1 (fn [attributes]
                                                   (merge declarations attributes))))))

(defn- apply-style-attributes [data]
  (let [style (-> data second :style)]
    (if style
      (loop [[style-attribute & attributes] [:stroke :stroke-width :stroke-dasharray :stroke-miterlimit
                                             :stroke-opacity :stroke-dashoffset :stroke-linecap :stroke-linejoin
                                             :fill :fill-opacity :fill-rule :opacity :color :visibility
                                             :mask :display]
             element data]
        (if style-attribute
          (recur attributes
                 (cond-> element
                   (get style style-attribute) (assoc-in [1 style-attribute] (get style style-attribute))))
          element))
      data)))

(defn- apply-css-rules [data css-rules]
  (if (and (vector? data)
           (-> data second map?))
    (apply-style-attributes
     (loop [result data
            [rule & remaining-rules] css-rules]
       (let [new-result (apply-css-rule result rule)]
         (if (seq remaining-rules)
           (recur new-result remaining-rules)
           new-result))))
    data))

(defn process-style-blocks [svg-data]
  (let [css-rules (read-css-rules svg-data)
        ordered-css-rules (reverse css-rules)]
    (walk/postwalk #(apply-css-rules % ordered-css-rules) svg-data)))

(defn- strip-elements [data tags]
  (walk/postwalk (fn [value]
                   (when-not (and (vector? value)
                                  (contains? tags (first value)))
                     value))
                 data))

(defn- strip-classes-and-ids [data]
  (walk/postwalk (fn [value]
                   (if (map? value)
                     (dissoc value :class :id)
                     value))
                 data))

(defn- replace-switch-elements [data]
  (walk/postwalk (fn [value]
                   (if (and (vector? value)
                            (= (first value) :switch))
                     (let [second-element (when (-> value count (> 1))
                                            (get value 1))
                           attributes (when (map? second-element)
                                        second-element)]
                       [:g attributes (last value)])
                     value))
                 data))

(defn- strip-currentcolor [data]
  (walk/postwalk (fn [value]
                   (if (map? value)
                     (into {}
                           (remove (fn [[_ v]]
                                     (and (string? v)
                                          (-> v str/lower-case (= "currentcolor")))))
                           value)
                     value))
                 data))

(def ^:private allowed-elements
  #{"svg" "g" "defs" "symbol" "use" "switch" "title" "desc"
    "path" "rect" "circle" "ellipse" "line" "polyline" "polygon"
    "text" "tspan" "textpath"
    "lineargradient" "radialgradient" "stop" "pattern" "clippath" "mask" "marker"
    "filter" "image"})

(defn- local-name [k]
  (-> (name k)
      str/lower-case
      (str/replace #"^[^:]*:" "")))

(defn- allowed-element? [tag]
  (let [element (local-name tag)]
    (or (contains? allowed-elements element)
        ;; filter primitives: feGaussianBlur, feOffset, ...
        (str/starts-with? element "fe"))))

(defn- allowed-attribute? [[k v]]
  (let [attribute (local-name k)]
    (cond
      (str/starts-with? attribute "on") false
      (= attribute "href") (and (string? v)
                                (or (str/starts-with? v "#")
                                    (str/starts-with? v "data:image/")))
      :else true)))

(defn sanitize-charge-data
  "Keeps only the SVG elements and attributes needed to draw a charge. Charge
  data is user-supplied and gets rendered on the site and in exports."
  [data]
  (util.walk/postwalk (fn [value]
                        (cond
                          (map-entry? value) value
                     ;; rebuilt only when something goes, so the walk can reuse it
                          (map? value) (if (every? allowed-attribute? value)
                                         value
                                         (into {} (filter allowed-attribute?) value))
                          (not (and (vector? value)
                                    (keyword? (first value)))) value
                     ;; links wrap drawn content, which stays as a group
                          (= (local-name (first value)) "a") (assoc value 0 :g)
                          (not (allowed-element? (first value))) nil
                          :else value))
                      data))

(defn strip-unnecessary-parts [svg-data]
  (-> svg-data
      (strip-elements #{:style :foreignObject :foreign-object :foreignobject})
      replace-switch-elements
      strip-classes-and-ids
      strip-currentcolor))

(defn add-ids [data]
  (walk/postwalk (fn [value]
                   (cond-> value
                     (and (vector? value)
                          (-> value second map?)
                          (-> value second :fill)) (assoc-in [1 :id] (uid/generate "id"))))
                 data))

(defn strip-ids [data]
  (walk/postwalk (fn [value]
                   (cond-> value
                     (and (vector? value)
                          (-> value second map?)) (update 1 dissoc :id)))
                 data))

(defn strip-clip-paths [data]
  (util.walk/postwalk (fn [value]
                        (if (and (vector? value)
                                 (= (first value) :clip-path)
                                 (not= value [:clip-path nil]))
                          [:clip-path nil]
                          value))
                      data))

(defn fix-stroke-and-fill [data]
  ;; add fill and stroke at top level as default
  ;; some SVGs don't specify them for elements if
  ;; they are black, but for that to work we need
  ;; the root element to specify them
  ;; disadvantage: this colour will now always show
  ;; im the interface, even if the charge doesn't
  ;; contain and black elements, but they usually will
  ;;
  ;; the stroke-width is also set to 0, because areas
  ;; that really should not get an outline otherwise
  ;; would default to one of width 1
  (let [new-data (assoc data 1 {:fill "#000000"
                                :stroke "none"
                                :stroke-width 0
                                :style {:clip-rule "evenodd"}})]
    (walk/postwalk
     (fn [data]
       ;; as a follow up to above comment:
       ;; if we find an element that has a stroke-width but no
       ;; stroke, then set that stroke to none, so those thick
       ;; black outlines won't appear
       ;; TODO: this might, for some SVGs, remove some outlines,
       ;; namely if the element was supposed to inherit the stroke
       ;; from a parent element
       (if (and (map? data)
                (contains? data :stroke-width)
                (not (contains? data :stroke)))
         (assoc data :stroke "none")
         data))
     new-data)))
